import { HistoryComponent } from './history/history.component';
import { EmailTemplatesComponent } from './email-templates/email-templates.component';
import { NgModule, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { ClientService } from 'src/app/base/services/client/client.service';
import { AuthGuard } from 'src/app/base/guards/auth.guard';
import * as pages from '../../core/routing/page-names';
import { ConfigurationsComponent } from './configurations/configurations.component';
import { ExportConfigurationRoutesService } from './export-configuration-routes.service';
import { ConfigurationTemplatesComponent } from './configuration-templates/configuration-templates.component';
import { FieldAssignmentTemplatesComponent } from './field-assignment-templates/field-assignment-templates.component';
import { CanDeactivateGuard } from 'src/app/base/guards/can-deactivate-guard.service';
import { ActivationEmailTemplatesComponent } from './activation-email-templates/activation-email-templates.component';
import { ActivationEmailTemplatesOverviewService } from './shared/activation-email-templates-overview/activation-email-templates-overview.service';
import { ExportConfigurationMasterDataServiceService } from './export-configuration-master-data.service';
import { FieldValueReplacementsComponent } from './field-value-replacements/field-value-replacements.component';
import { FieldValueReplacementService } from './field-value-replacements/field-value-replacement.service';
import { RoleService } from 'src/app/base/services/role.service';
import { OutputEmailAccountsService } from './activation-email-templates/output-email-accounts.service';
import { ConfigurationInterfaceGuard } from 'src/app/base/guards/configuration-interface.guard';

const routes: Routes = [
  {
    path: pages.exportConfigurationPageName,
    canActivate: [ConfigurationInterfaceGuard],
    resolve: {
      data1: () => { return inject(ExportConfigurationRoutesService).initializeNavigationData(); },
      data2: () => { return inject(ActivationEmailTemplatesOverviewService).loadInitialData(); },
      data3: () => { return inject(ExportConfigurationMasterDataServiceService).loadSourceFields(); }
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: pages.configurationsPageName,
      },
      {
        path: pages.configurationsPageName,
        component: ConfigurationsComponent,
        resolve: {
          data: () => { return inject(ClientService).getAccessibleClients(); },
          data4: () => { return inject(RoleService).getRoleMappingInfo(); },
          accounts: () => { return inject(OutputEmailAccountsService).loadOutputEmailAccounts(); }
        },
        canDeactivate: [(component: ConfigurationsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ConfigurationsComponent
          },
          {
            path: ":id",
            component: ConfigurationsComponent,
            canDeactivate: [(component: ConfigurationsComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: pages.configurationGeneralPageName
              },
              {
                path: pages.configurationGeneralPageName,
                component: ConfigurationsComponent,
                resolve: {
                  roleInfo:()=>{return inject(RoleService).getRoleMappingInfo();}
                }
              },
              {
                path: pages.configurationBacklogPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationFinancePageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationTimeSchedulePageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationEmailPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationSftpPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationFtpPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationRestPageName,
                component: ConfigurationsComponent,
              },
              {
                path: pages.configurationFilePageName,
                component: ConfigurationsComponent,
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: pages.configurationFileGeneralPageName
                  },
                  {
                    path: pages.configurationFileGeneralPageName,
                    component: ConfigurationsComponent,
                  },
                  {
                    path: pages.configurationFileColumnBasedPageName,
                    component: ConfigurationsComponent,
                  },
                  {
                    path: pages.configurationFileLineBasedPageName,
                    component: ConfigurationsComponent,
                  },
                  {
                    path: pages.configurationFileFilterPageName,
                    component: ConfigurationsComponent,
                  },
                ]
              },
            ]
          }
        ]
      },
      {
        path: pages.fieldValueReplacementsPageName,
        canActivate: [ConfigurationInterfaceGuard],
        component: FieldValueReplacementsComponent,
        data: () => { return inject(FieldValueReplacementService).getFieldValueReplacementClients(); },
        resolve: {
          data1: () => { return inject(ClientService).getAccessibleClients(); },
          data2: () => { return inject(FieldValueReplacementService).getFieldValueReplacementClients(); },
          data3: () => { return inject(ExportConfigurationMasterDataServiceService).loadSourceFields(); }
        },
      },
      {
        path: pages.configurationTemplatesPageName,
        canActivate: [ConfigurationInterfaceGuard],
        canDeactivate: [(component: ConfigurationTemplatesComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
        component: ConfigurationTemplatesComponent,
        data: { "initialize": true }, //this is added to differentiate the loading of overview component between menu click and button click in the fomrs
        resolve: {
          data: () => { return inject(ClientService).getAccessibleClients(); },
          accounts: () => { return inject(OutputEmailAccountsService).loadOutputEmailAccounts(); }
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ConfigurationTemplatesComponent
          },
          {
            path: ":id",
            component: ConfigurationTemplatesComponent,
            canDeactivate: [(component: ConfigurationTemplatesComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: pages.configurationGeneralPageName
              },
              {
                path: pages.configurationGeneralPageName,
                component: ConfigurationTemplatesComponent,
                resolve: {
                  roleInfo:()=>{return inject(RoleService).getRoleMappingInfo();}
                }
              },
              {
                path: pages.configurationBacklogPageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationFinancePageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationTimeSchedulePageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationEmailPageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationSftpPageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationFtpPageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationRestPageName,
                component: ConfigurationTemplatesComponent,
              },
              {
                path: pages.configurationFilePageName,
                component: ConfigurationTemplatesComponent,
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: pages.configurationFileGeneralPageName
                  },
                  {
                    path: pages.configurationFileGeneralPageName,
                    component: ConfigurationTemplatesComponent,
                  },
                  {
                    path: pages.configurationFileColumnBasedPageName,
                    component: ConfigurationTemplatesComponent,
                  },
                  {
                    path: pages.configurationFileLineBasedPageName,
                    component: ConfigurationTemplatesComponent,
                  },
                  {
                    path: pages.configurationFileFilterPageName,
                    component: ConfigurationTemplatesComponent,
                  },
                ]
              },
            ]
          }
        ]
      },
      {
        path: pages.fieldAssignmentTemplatesPageName,
        component: FieldAssignmentTemplatesComponent,
        canActivate: [ConfigurationInterfaceGuard],
        canDeactivate: [(component: FieldAssignmentTemplatesComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: FieldAssignmentTemplatesComponent,
          },
          {
            path: ':id',
            component: FieldAssignmentTemplatesComponent,
          }
        ]
      },
      {
        path: pages.emailTemplatesPageName,
        component: EmailTemplatesComponent,
        canActivate: [ConfigurationInterfaceGuard],
        canDeactivate: [(component: EmailTemplatesComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
        resolve: {
          accounts: () => { return inject(OutputEmailAccountsService).loadOutputEmailAccounts(); }
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: EmailTemplatesComponent,
          },
          {
            path: ':id',
            component: EmailTemplatesComponent,
          }
        ]
      },
      {
        path: pages.activationEmailTemplatesPageName,
        component: ActivationEmailTemplatesComponent,
        canActivate: [ConfigurationInterfaceGuard],
        canDeactivate: [(component: ActivationEmailTemplatesComponent) => inject(CanDeactivateGuard).canDeactivate(component)],
        resolve: {
          accounts: () => { return inject(OutputEmailAccountsService).loadOutputEmailAccounts(); }
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: ActivationEmailTemplatesComponent,
          },
          {
            path: ':id',
            component: ActivationEmailTemplatesComponent,
          }
        ]
      },
      {
        path: pages.historyPageName,
        component: HistoryComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
  //providers: [OutputEmailAccountsService]
})
export class ExportConfigurationRoutingModule {

}
